import {useEffect, useEffectEvent} from 'react';

import {mergeBuyerWithDefaults, parseBuyerFieldKey} from '../utils';
import {createEmptyBuyerFormEntry} from '../validation';

import type {CreateBuyerFormValues} from '../validation';
import type {AiSuggestion} from '@/features/ai-extraction';
import type {UseFieldArrayAppend, UseFieldArrayReplace, UseFormReturn} from 'react-hook-form';

interface UseSyncBuyersWithAiFieldsParams {
  form: UseFormReturn<CreateBuyerFormValues>;
  aiFields: Map<string, AiSuggestion>;
  fieldsLength: number;
  append: UseFieldArrayAppend<CreateBuyerFormValues, 'buyers'>;
  replace: UseFieldArrayReplace<CreateBuyerFormValues, 'buyers'>;
}

/** Grows the array to fit every AI-detected co-buyer; replace() is safe since extraction only fills empty fields. */
export function useSyncBuyersWithAiFields({
  form,
  aiFields,
  fieldsLength,
  append,
  replace
}: UseSyncBuyersWithAiFieldsParams): void {
  let neededLength = 0;
  for (const key of aiFields.keys()) {
    const parsed = parseBuyerFieldKey(key);
    if (parsed) neededLength = Math.max(neededLength, parsed.index + 1);
  }

  const growBuyers = useEffectEvent(() => {
    const currentBuyers = form.getValues('buyers');
    if (currentBuyers.length >= neededLength) {
      // setValue into a missing index leaves holes, which Array.map would skip.
      replace(Array.from(currentBuyers, buyer => mergeBuyerWithDefaults(buyer)));
      return;
    }
    // One call: RHF reads the stale default list for each append until the form has mounted.
    const missing = Array.from({length: neededLength - currentBuyers.length}, () =>
      createEmptyBuyerFormEntry()
    );
    append(missing, {shouldFocus: false});
  });

  useEffect(() => {
    if (fieldsLength < neededLength) growBuyers();
  }, [fieldsLength, neededLength]);
}
